import { Component, Inject, OnInit, Optional } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { timeStamp } from "console";
import { ToastrService } from "ngx-toastr";
import { FabbricatiFormComponent } from "src/app/anagrafiche/fabbricati/dialogs/form/form.component";
import { AltroSoggetto } from "src/app/models/altro-soggetto";
import { Fabbricato } from "src/app/models/fabbricato";
import { Incarico } from "src/app/models/incarico";
import { Polizza } from "src/app/models/polizza";
import { FabbricatoService } from "src/app/services/fabbricato.service";
import { IncaricoService } from "src/app/services/incarico.service";
import { PolizzaService } from "src/app/services/polizza.service";
import { BaseEditForm } from "src/app/shared/baseEditForm";
import { findInRoute } from "src/app/shared/utilities";

@Component({
  selector: "polizze-form",
  templateUrl: "./form.component.html",
  styleUrls: ["./form.component.scss"],
})
export class PolizzeFormComponent extends BaseEditForm implements OnInit {
  polizza: Polizza;
  form: FormGroup;
  incarico: Incarico;
  dataStore: any;
  isLoadingGeneraliData = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private incaricoService: IncaricoService,
    private polizzaService: PolizzaService,
    private fabbricatoService: FabbricatoService,
    private dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<PolizzeFormComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();

    this.polizza = data?.search;
    this.incarico = data?.incarico;
  }

  async ngOnInit() {
    const idPolizza = findInRoute("idPolizza", this.route);
    if (idPolizza) {
      this.polizza = await this.polizzaService.getOne(+idPolizza, [
        "compagnia",
        "agenzia",
        "ramo",
        "contraente",
        "fabbricato",
        "contraente.comune",
        "cga",
      ]);
    }

    this.initializeForm();

    if (!this.polizza && this.incarico) {
      await this.loadGeneraliData();
    }
  }

  private initializeForm() {
    this.form = this.fb.group({
      numero: [this.polizza?.numero],
      compagnia: [this.polizza?.compagnia],
      agenzia: [this.polizza?.agenzia],
      ramo: [this.polizza?.ramo],
      contraente: [this.polizza?.contraente],
      data_decorrenza: [this.polizza?.data_decorrenza],
      fabbricato: [this.polizza?.fabbricato],
      sottocompagnia: [this.polizza?.sottocompagnia],
      cga: [this.polizza?.cga],
    });
  }

  private async loadGeneraliData() {
    this.isLoadingGeneraliData = true;

    try {
      const res = await this.incaricoService.getDataStoreFromIncarico(
        this.incarico.id
      );

      if (res) {
        this.form.patchValue({
          numero: res.numeroPolizza,
          compagnia: res.compagnia,
          agenzia: res.agenzia,
          data_decorrenza: res.decorrenza,
        });
      }
    } catch (e) {
      this.toast.error("Errore nel caricamento dei dati Generali");
    } finally {
      this.isLoadingGeneraliData = false;
    }
  }

  async handleSuccessfulSubmit() {
    try {
      let result;
      const formData = { ...this.form.value };

      // Auto-create fabbricato if contraente is an AltroSoggetto and no fabbricato is set
      if (formData.contraente && !formData.fabbricato) {
        // Check if contraente is AltroSoggetto (check by type property or instanceof)
        const isAltroSoggetto =
          formData.contraente instanceof AltroSoggetto ||
          formData.contraente.type === "altroSoggetto" ||
          (formData.contraente.constructor &&
            formData.contraente.constructor.name === "AltroSoggetto");

        if (isAltroSoggetto) {
          try {
            // Debug: log contraente object
            console.log("Contraente object:", formData.contraente);

            // Create a new Fabbricato based on contraente data
            // Build name from properties directly since contraente might be a plain object
            let fabbricatoNome = "FABBRICATO DI ";
            const nome =
              formData.contraente.nome || formData.contraente.fullName || "";
            const cognome = formData.contraente.cognome || "";

            if (nome || cognome) {
              fabbricatoNome += nome + (cognome ? " " + cognome : "");
            } else if (formData.contraente.humanIdentifier) {
              fabbricatoNome += formData.contraente.humanIdentifier;
            } else {
              // Last resort - use ID
              fabbricatoNome += "ID " + (formData.contraente.id || "N/A");
            }

            const fabbricatoData: any = {
              amministratore_id: null, // Not linked to any amministratore
              nome: fabbricatoNome,
              via: this.incarico?.via || "",
              civico: this.incarico?.civico || "",
              comune: this.incarico?.comune || "",
              cap: this.incarico?.cap || "",
              unita_misura_estensione_id: 1,
              partita_iva: formData.contraente.partita_iva,
              codice_fiscale: formData.contraente.codice_fiscale,
              IBAN: formData.contraente.IBAN,
              note: formData.contraente.note,
              tel: formData.contraente.tel,
              cell: formData.contraente.cell,
              email: formData.contraente.email,
            };

            const newFabbricato = await this.fabbricatoService.create(
              fabbricatoData
            );
            formData.fabbricato = newFabbricato;
            this.toast.info(
              "Fabbricato creato automaticamente per il contraente"
            );
          } catch (error) {
            console.error("Error creating fabbricato:", error);
            this.toast.warning(
              "Impossibile creare automaticamente il fabbricato"
            );
          }
        }
      }

      if (this.polizza?.id) {
        Object.assign(this.polizza, formData);
        result = await this.polizzaService.edit(this.polizza);
      } else {
        result = await this.polizzaService.create(formData);
        this.polizza = result;
      }

      this.toast.success("Polizza salvata");

      if (this.dialogRef) {
        this.dialogRef.close(result);
      }
    } catch (e) {
      this.toast.error("Impossibile salvare la polizza");
      console.error(e);
    }
  }

  aggiungiFabbricatoDelContraente() {
    if (this.polizza?.fabbricato)
      return this.toast.warning(
        "La polizza ha già un fabbricato assegnato.",
        "Attenzione!"
      );

    if (!this.polizza?.contraente)
      return this.toast.warning(
        "Impossibile creare il fabbricato del contraente: la polizza non ha un contraente.",
        "Attenzione!"
      );

    let fabbricato = new Fabbricato();
    let contraente = this.polizza?.contraente;
    fabbricato.nome =
      "FABBRICATO DI " + this.polizza?.contraente?.humanIdentifier;
    // For AltroSoggetto, use address from incarico if available
    const isAltroSoggetto = contraente instanceof AltroSoggetto;
    fabbricato.via =
      isAltroSoggetto && this.incarico?.via
        ? this.incarico.via
        : contraente?.via;
    fabbricato.civico =
      isAltroSoggetto && this.incarico?.civico
        ? this.incarico.civico
        : contraente?.civico;
    fabbricato.comune =
      isAltroSoggetto && this.incarico?.comune
        ? this.incarico.comune
        : contraente?.comune;

    const dialogData = { ...fabbricato, polizza: this.polizza };
    let dialogRef = this.dialog.open(FabbricatiFormComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(async (v) => {
      if (v?.id) {
        this.form.controls["fabbricato"].setValue(v);
      } else this.toast.warning("Fabbricato del contraente non aggiunto");
    });
  }

  shouldShowFabbricatoField() {
    if (!this.polizza)
      // Non posso mostrare il campo del fabbricato collegato alla polizza finchè non so se il contraente della polizza è un altro soggetto
      return false;

    if (this.polizza.contraente instanceof AltroSoggetto) return true;
  }

  shouldShowAddFabbricatoButton() {
    return this.shouldShowFabbricatoField() && !this.polizza.fabbricato;
  }
}
